import React, { useEffect, useState } from 'react';
import {
    Box,
    ButtonBase,
    Collapse,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import moment from 'moment';
import Routes from '../../routes';
import { trakrefFontFamily } from '../../utils';
import { Grey1, Grey3 } from '../../color_utils';

const fontSx = { fontFamily: trakrefFontFamily };

const TYPE_DOT_COLORS = {
    'Locations': '#21B8BF',
    'Inventory': '#0E8C9C',
    'Users': '#673AB7',
    'Service Events': '#F58D23',
};

const STATUS_PILLS = {
    Completed: { backgroundColor: '#EAF6E1', color: '#3D6B0E', dot: '#89C826' },
    Partial: { backgroundColor: '#FFF3E0', color: '#9A5B00', dot: '#FF9800' },
    Failed: { backgroundColor: '#FDECEC', color: '#A01E1E', dot: '#FF595E' },
};

const headerCellSx = {
    ...fontSx,
    fontSize: '11px',
    fontWeight: 500,
    color: Grey3,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    whiteSpace: 'nowrap',
};

const bodyCellSx = { ...fontSx, fontSize: '13px', color: Grey1, whiteSpace: 'nowrap' };

const StatusPill = ({ status }) => {
    const pill = STATUS_PILLS[status] || STATUS_PILLS.Failed;
    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                height: 22,
                padding: '0 10px',
                borderRadius: '11px',
                fontSize: '12px',
                fontWeight: 700,
                fontFamily: trakrefFontFamily,
                backgroundColor: pill.backgroundColor,
                color: pill.color,
            }}
        >
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: pill.dot }} />
            {status}
        </Box>
    );
};

const ImportRecentUploads = () => {
    const [expanded, setExpanded] = useState(false);
    const [uploads, setUploads] = useState([]);

    useEffect(() => {
        const fetchUploads = async () => {
            try {
                const response = await fetch(Routes.CustomerDataImport.RecentUploads(), {
                    method: 'GET',
                    headers: { 'Accept': 'application/json' },
                });
                if (!response.ok) return;
                const json = await response.json();
                const payload = json?.data ?? json;
                if (Array.isArray(payload)) setUploads(payload);
            } catch (e) {
                // history endpoint unavailable — panel just shows zero uploads
            }
        };
        fetchUploads();
    }, []);

    return (
        <Paper variant="outlined" sx={{ maxWidth: 1320, marginTop: '44px', borderRadius: '6px', overflow: 'hidden' }}>
            <ButtonBase
                onClick={() => setExpanded(prev => !prev)}
                aria-expanded={expanded}
                sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '14px 16px',
                    '&:hover': { backgroundColor: '#F8F7F7' },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Typography sx={{ ...fontSx, fontSize: '14px', fontWeight: 500, color: Grey1 }}>
                        Recent uploads
                    </Typography>
                    <Box
                        sx={{
                            fontSize: '11px',
                            fontWeight: 600,
                            fontFamily: trakrefFontFamily,
                            color: Grey3,
                            backgroundColor: '#EFF2F2',
                            borderRadius: '10px',
                            padding: '1px 8px',
                        }}
                    >
                        {uploads.length}
                    </Box>
                </Box>
                <ExpandMoreIcon
                    sx={{
                        color: Grey3,
                        transition: 'transform 0.18s',
                        transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                    }}
                />
            </ButtonBase>
            <Collapse in={expanded}>
                <Box sx={{ borderTop: '1px solid #E1E9ED', overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={headerCellSx}>File</TableCell>
                                <TableCell sx={headerCellSx}>Type</TableCell>
                                <TableCell sx={headerCellSx}>Date</TableCell>
                                <TableCell sx={headerCellSx}>Imported by</TableCell>
                                <TableCell sx={headerCellSx}>Rows</TableCell>
                                <TableCell sx={headerCellSx}>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {uploads.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} sx={{ ...bodyCellSx, color: Grey3, textAlign: 'center', padding: '20px' }}>
                                        No uploads yet.
                                    </TableCell>
                                </TableRow>
                            )}
                            {uploads.map((upload, index) => (
                                <TableRow key={`${upload.fileName}-${index}`} hover>
                                    <TableCell sx={bodyCellSx}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 500 }}>
                                            <InsertDriveFileOutlinedIcon sx={{ fontSize: 18, color: Grey3 }} />
                                            {upload.fileName}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={bodyCellSx}>
                                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}>
                                            <Box
                                                sx={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    backgroundColor: TYPE_DOT_COLORS[upload.importType] || Grey3,
                                                }}
                                            />
                                            {upload.importType}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ ...bodyCellSx, color: Grey3 }}>
                                        {upload.uploadDate && moment(upload.uploadDate).isValid()
                                            ? moment(upload.uploadDate).format('MMM D, YYYY · h:mm A')
                                            : ''}
                                    </TableCell>
                                    <TableCell sx={{ ...bodyCellSx, color: Grey3 }}>{upload.importedBy}</TableCell>
                                    <TableCell sx={bodyCellSx}>{`${upload.importedRows} / ${upload.totalRows}`}</TableCell>
                                    <TableCell sx={bodyCellSx}>
                                        <StatusPill status={upload.status} />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            </Collapse>
        </Paper>
    );
};

export default ImportRecentUploads;
